import {
  decomposePropertiesWithDefinitionsAndNesting,
  AtomicValueWithNesting,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import {
  LeadingStyleGroup,
  StylesGroup,
  ComparisonMode,
  StylesDerivedNativeActionNames,
  CssCustomPropertyGroupStyleItem,
  ValueSetValueFilter,
} from '../../types';
import { CssCustomProperties } from '@wix/component-protocol/schema';
import { CssPropertyEnumCssPropertyType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { getAllowedPropertiesForAction } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import {
  getEquivalentFamily,
  getEquivalentFamilyId,
} from './propertyEquivalents';
import { STYLE_NAMESPACE } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/types';

type ResolvedValues = Partial<
  Record<CssPropertyEnumCssPropertyType, AtomicValueWithNesting[]>
>;

export type GroupDataDirectProps = {
  comparisonMode: ComparisonMode.DirectProps;
  group: StylesGroup;
  resolvedValues: ResolvedValues;
};

export type GroupDataValueSet = {
  comparisonMode: ComparisonMode.ValueSet;
  group: StylesGroup;
  resolvedValues: ResolvedValues;
  valueSet: Map<string, Set<string>>;
};

export type GroupData = GroupDataDirectProps | GroupDataValueSet;

const getStylesRecord = (
  cssCustomProperties: CssCustomProperties,
  values: Record<string, string>,
): Record<string, string> => {
  const stylesRecord: Record<string, string> = {};

  Object.entries(cssCustomProperties).forEach(([key, customProperty]) => {
    const propertyValue = values[key];

    if (propertyValue && customProperty.cssPropertyType) {
      stylesRecord[
        customProperty.cssPropertyType as CssPropertyEnumCssPropertyType
      ] = propertyValue;
    }
  });

  return stylesRecord;
};

/**
 * Extracts values per atomic key from resolved values for value-set based comparison.
 * Used for sibling property groups where we need per-property value tracking.
 * Filters out empty arrays as they represent capability without actual values.
 *
 * Returns a map to enable:
 * - Cross-sibling matching: borderTopColor: 'red' matches borderBottomColor: 'red'
 * - Prevent false matches: borderWidth: "1px" vs borderWidth: "1px 2px" are different
 *
 * @internal - Exported for testing purposes
 */
export const extractValueSet = (
  resolvedValues: ResolvedValues,
  valueFilter?: (key: string, value: string) => boolean,
): Map<string, Set<string>> => {
  const valueSetMap = new Map<string, Set<string>>();

  for (const [key, atomicValues] of Object.entries(resolvedValues)) {
    if (atomicValues && atomicValues.length > 0) {
      // Use original value to preserve CSS variable semantics in grouping
      // e.g., var(--primary) and var(--secondary) are different groups even if resolved to same color
      const valueSet = new Set<string>();
      atomicValues.forEach((atomicValue) => {
        if (!valueFilter || valueFilter(key, atomicValue.value)) {
          valueSet.add(atomicValue.value);
        }
      });
      if (valueSet.size > 0) {
        valueSetMap.set(key, valueSet);
      }
    }
  }

  return valueSetMap;
};

/**
 * Group properties by their valueEquivalent groups and collect their values
 */
function groupByValueEquivalents(
  values: Map<string, Set<string>>,
): Map<string, Map<string, Set<string>>> {
  const grouped = new Map<string, Map<string, Set<string>>>();

  for (const [key, valueSet] of values) {
    const groupId = getEquivalentFamilyId(getEquivalentFamily(key));

    if (!grouped.has(groupId)) {
      grouped.set(groupId, new Map());
    }
    grouped.get(groupId)!.set(key, valueSet);
  }

  return grouped;
}

/**
 * Count occurrences of each value within a property group
 */
function countValuesInGroup(
  props: Map<string, Set<string>>,
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const valueSet of props.values()) {
    for (const value of valueSet) {
      counts.set(value, (counts.get(value) || 0) + 1);
    }
  }

  return counts;
}

/**
 * Returns true if subset's value sets are compatible with superset's.
 * Used for sibling-based comparison with direction-agnostic value tracking.
 *
 * Rules (direction-agnostic):
 * 1. Each value used by subset must exist somewhere in superset's valueEquivalent group
 *    (doesn't matter which specific direction key holds it; per-key matching is intentionally ignored)
 * 2. For each distinct value within a valueEquivalent group, subset must have ≤ occurrences than superset
 *    (counts occurrences across all keys in the group, not per-key)
 *
 * Examples:
 * - Uniform border (4× '8px') ⊄ Non-uniform border (2× '8px' + 2× '1px') ✗ (more occurrences)
 * - borderTopColor: 'red' ⊆ {borderBottomColor: 'red'} ✓ (cross-sibling, direction-agnostic)
 * - {borderTopWidth: '1px', borderLeftWidth: '1px'} ⊆ {borderBottomWidth: '1px', borderRightWidth: '1px'} ✓
 *   (same value counts, different sides - direction-agnostic)
 *
 * @internal - Exported for testing purposes
 */
export const isValueSetSubset = (
  supersetValues: Map<string, Set<string>>,
  subsetValues: Map<string, Set<string>>,
): boolean => {
  // Empty maps should not be treated as subsets to avoid unexpected mutations
  if (subsetValues.size === 0 || supersetValues.size === 0) {
    return false;
  }

  // Group properties by their valueEquivalent groups
  const subsetByEquivGroup = groupByValueEquivalents(subsetValues);
  const supersetByEquivGroup = groupByValueEquivalents(supersetValues);

  // Check each valueEquivalent group in subset
  for (const [groupId, subsetProps] of subsetByEquivGroup) {
    const supersetProps = supersetByEquivGroup.get(groupId);

    if (!supersetProps) {
      // Subset has an entire valueEquivalent group that superset doesn't have
      return false;
    }

    // Collect all values available in this superset valueEquivalent group
    const supersetGroupValues = new Set<string>();
    for (const valueSet of supersetProps.values()) {
      for (const value of valueSet) {
        supersetGroupValues.add(value);
      }
    }

    // Rule 1: Direction-agnostic compatibility check
    for (const subsetValueSet of subsetProps.values()) {
      for (const value of subsetValueSet) {
        if (!supersetGroupValues.has(value)) {
          return false;
        }
      }
    }

    // Rule 2: Count-based check within the valueEquivalent group
    // Subset must have ≤ occurrences of each value compared to superset
    const supersetValueCounts = countValuesInGroup(supersetProps);
    const subsetValueCounts = countValuesInGroup(subsetProps);

    for (const [value, subsetCount] of subsetValueCounts) {
      const supersetCount = supersetValueCounts.get(value) || 0;
      if (subsetCount > supersetCount) {
        // Subset has MORE occurrences of this value than superset → NOT a subset
        return false;
      }
    }
  }

  return true;
};

const collectGroupsData = (
  groups: StylesGroup[],
  comparisonMode: ComparisonMode = ComparisonMode.DirectProps,
  actionName: StylesDerivedNativeActionNames,
  valueSetValueFilter?: ValueSetValueFilter,
): GroupData[] => {
  return groups.reduce<GroupData[]>((acc, group) => {
    const styles = group.context.query.getStyles();
    const allowedStyles = new Set(
      getAllowedPropertiesForAction(actionName),
    ) as Set<string>; // TODO: type properly;

    let relevantStyles: Record<string, string>;
    let relevantDefinitions: string[];

    if (group.styleNamespace === STYLE_NAMESPACE.CssCustomProperties) {
      // For cssCustomProperties, the key is a custom ID (e.g., '--my-border-theme')
      // We need to check cssPropertyType from definitions, not the key itself
      const styleDefinitions = group.items.reduce(
        (accum, cur: CssCustomPropertyGroupStyleItem) => {
          if (cur.styleItem.cssPropertyType) {
            accum[cur.styleItemId] = cur.styleItem.cssPropertyType;
          }

          return accum;
        },
        {} as Record<string, string>,
      );

      // Extract relevant definitions (property types)
      relevantDefinitions = Object.entries(styleDefinitions)
        .filter(([_, cssPropertyType]) => allowedStyles.has(cssPropertyType))
        .map(([_, cssPropertyType]) => cssPropertyType);

      relevantStyles = Object.fromEntries(
        Object.entries(styles.cssCustomProperties).filter(([key]) => {
          const cssPropertyType = styleDefinitions[key];
          return cssPropertyType && allowedStyles.has(cssPropertyType);
        }),
      );
    } else {
      // For cssProperties, the key IS the CSS property type
      // Extract relevant definitions from group items
      relevantDefinitions = group.items
        .map((item) => item.styleItemId)
        .filter((key) => allowedStyles.has(key));

      relevantStyles = Object.fromEntries(
        Object.entries(styles.cssProperties).filter(([key]) => {
          return allowedStyles.has(key);
        }),
      );
    }

    // Convert to common format for decomposition
    const valuesToDecompose =
      group.styleNamespace === STYLE_NAMESPACE.CssCustomProperties
        ? getStylesRecord(
            Object.fromEntries(
              group.items.map((item) => [item.styleItemId, item.styleItem]),
            ),
            relevantStyles,
          )
        : relevantStyles;

    const resolvedValues = decomposePropertiesWithDefinitionsAndNesting(
      relevantDefinitions,
      valuesToDecompose,
    );

    // Create discriminated union based on provided comparison mode
    if (comparisonMode === ComparisonMode.ValueSet) {
      const meaningfulFilter = valueSetValueFilter
        ? (key: string, value: string) =>
            valueSetValueFilter({ propertyType: key, value })
        : undefined;

      acc.push({
        comparisonMode: ComparisonMode.ValueSet,
        group,
        resolvedValues,
        valueSet: extractValueSet(resolvedValues, meaningfulFilter),
      });
    } else {
      acc.push({
        comparisonMode: ComparisonMode.DirectProps,
        group,
        resolvedValues,
      });
    }

    return acc;
  }, []);
};

/**
 * Returns true if every key/value in subset exists with the same value in superset.
 * Supports two comparison modes:
 * - DirectProps: Direct property matching with capability checking
 * - ValueSet: Unique value set matching (for sibling properties)
 *
 * Capability-aware subset detection:
 * - Checks both definitions (empty arrays) and actual values
 * - Subset cannot have capabilities that superset doesn't have
 * - [] === [] means both have capability with no value (valid subset)
 * - [values] !== [] (different states)
 * - Empty groups with same definitions are valid subsets
 *
 * Additional rules:
 * - DirectProps mode: If subset has more keys than superset, not a subset.
 * - ValueSet mode: Property count doesn't matter, only unique values (empty arrays filtered out).
 */
export const isSubset = (
  superset: ResolvedValues,
  subset: ResolvedValues,
  mode: ComparisonMode = ComparisonMode.DirectProps,
): boolean => {
  // Value-set mode: compare value sets per atomic key (excluding empty arrays)
  if (mode === ComparisonMode.ValueSet) {
    const supersetValues = extractValueSet(superset);
    const subsetValues = extractValueSet(subset);
    // If both have no actual values, check if they have same capabilities
    if (subsetValues.size === 0 && supersetValues.size === 0) {
      // Both have only empty arrays - compare capabilities by checking keys
      const subsetKeys = Object.keys(subset);

      // Empty subset is not valid
      if (subsetKeys.length === 0) {
        return false;
      }
      // Check if all subset keys exist in superset
      return subsetKeys.every((key) => key in superset);
    }
    return isValueSetSubset(supersetValues, subsetValues);
  }

  // Direct-props mode: check key count
  const subsetKeys = Object.keys(subset);
  const supersetKeys = Object.keys(superset);

  // Empty objects are never subsets (no capabilities = not meaningful for subset relationship)
  if (subsetKeys.length === 0 || supersetKeys.length === 0) {
    return false;
  }

  // If subset has more keys, it cannot be a subset
  if (subsetKeys.length > supersetKeys.length) {
    return false;
  }

  // Check each key in subset
  for (const key of subsetKeys) {
    const k = key as CssPropertyEnumCssPropertyType;

    // Subset cannot have a capability that superset doesn't have
    if (!(k in superset)) {
      return false;
    }

    // Compare atomic values arrays
    const subsetValues = subset[k];
    const supersetValues = superset[k];

    // Subset values must exist (we're iterating subset keys)
    if (!subsetValues) {
      continue; // Should never happen, but satisfy TypeScript
    }

    // Subset cannot have a capability that superset doesn't have
    if (!supersetValues) {
      return false;
    }

    // If subset has no value (eligible but empty), it's always a valid subset
    // (superset can have any value or no value - doesn't matter)
    if (subsetValues.length === 0) {
      continue;
    }

    // Subset has a value - superset must also have a value (and it must match)
    if (supersetValues.length === 0) {
      // Subset has value but superset doesn't - NOT a subset
      return false;
    }

    // Both have values - compare original values to preserve CSS variable semantics (ignore nesting level)
    // e.g., var(--primary) !== var(--secondary) even if they resolve to the same color
    if (supersetValues[0].value !== subsetValues[0].value) {
      return false;
    }
  }

  return true;
};

/**
 * Pure function to transform pre-resolved group data into leaders with subsets.
 * Does not retain resolved values in the output.
 * Handles transitive subset relationships: if A absorbs B, and B had subsets,
 * those subsets are transferred to A.
 */
export const buildLeadingGroupsFromResolved = (
  groupsData: GroupData[],
): LeadingStyleGroup[] => {
  const sortedGroupsData = sortGroupsDataByStylesSize(groupsData);

  const processedGroups = new Set<GroupData>();
  const leaders: LeadingStyleGroup[] = [];
  // Track which groups are leaders and their subsets for transitive transfer
  const leaderMap = new Map<GroupData, StylesGroup[]>();
  // Reverse lookup map for O(1) access from StylesGroup to GroupData
  const groupToDataMap = new Map<StylesGroup, GroupData>();

  // Build reverse lookup map
  sortedGroupsData.forEach((groupData) => {
    groupToDataMap.set(groupData.group, groupData);
  });

  for (const leaderData of sortedGroupsData) {
    if (processedGroups.has(leaderData)) {
      continue;
    }

    const subsetGroups: StylesGroup[] = [];

    if (!leaderData.group.isExcluedFromMerging) {
      // Use the comparison mode from the discriminated union
      const mode = leaderData.comparisonMode;

      for (const candidate of sortedGroupsData) {
        if (
          candidate === leaderData ||
          processedGroups.has(candidate) ||
          candidate.comparisonMode !== mode // Only compare groups with same comparison mode
        ) {
          continue;
        }

        // For ValueSet mode, use the pre-filtered valueSet instead of re-extracting from resolvedValues
        // This ensures the valueSetValueFilter is correctly applied during subset detection
        const isSubsetMatch =
          mode === ComparisonMode.ValueSet
            ? isValueSetSubset(
                (leaderData as GroupDataValueSet).valueSet,
                (candidate as GroupDataValueSet).valueSet,
              )
            : isSubset(
                leaderData.resolvedValues,
                candidate.resolvedValues,
                mode,
              );

        if (isSubsetMatch) {
          processedGroups.add(candidate);
          subsetGroups.push(candidate.group);

          // TRANSITIVE TRANSFER: If candidate was a leader with subsets, transfer them
          if (leaderMap.has(candidate)) {
            const candidateSubsets = leaderMap.get(candidate)!;
            subsetGroups.push(...candidateSubsets);

            // Mark all transferred subsets as processed using O(1) lookup
            candidateSubsets.forEach((subset) => {
              const subsetData = groupToDataMap.get(subset);
              if (subsetData) {
                processedGroups.add(subsetData);
              }
            });

            // Remove candidate from leader map since it's now a subset
            leaderMap.delete(candidate);
          }
        }
      }
    }

    // Register this leader and its subsets
    leaderMap.set(leaderData, subsetGroups);

    leaders.push({
      ...leaderData.group,
      comparisonMode: leaderData.comparisonMode,
      subsetGroups,
    });
  }

  return leaders;
};

const sortGroupsDataByStylesSize = (groupsData: GroupData[]): GroupData[] => {
  const groupsDataWithMeta = groupsData.map((data, index) => {
    // Use DISTINCT unique values across all atomic keys for ValueSet mode, property count for DirectProps
    let uniqueValueCount: number;
    let totalOccurrences: number = 0;

    if (data.comparisonMode === ComparisonMode.ValueSet) {
      // Count DISTINCT unique values across all atomic keys (deduplicating across keys)
      const allUniqueValues = new Set<string>();
      for (const valueSet of data.valueSet.values()) {
        for (const value of valueSet) {
          allUniqueValues.add(value);
          totalOccurrences++; // Count total occurrences for secondary sort
        }
      }
      uniqueValueCount = allUniqueValues.size;
    } else {
      uniqueValueCount = Object.keys(data.resolvedValues).length;
    }

    const propCount = Object.keys(data.resolvedValues).length;

    return {
      index,
      uniqueValueCount,
      totalOccurrences,
      propCount,
      ...data,
    };
  });

  groupsDataWithMeta.sort((a, b) => {
    // Primary: Sort by unique value count descending (more diverse value sets first)
    if (b.uniqueValueCount !== a.uniqueValueCount) {
      return b.uniqueValueCount - a.uniqueValueCount;
    }

    // Secondary: For ValueSet mode, sort by total occurrences descending
    // This ensures the group with more value occurrences becomes the leader,
    // since subset detection compares occurrence counts
    if (
      a.comparisonMode === ComparisonMode.ValueSet &&
      b.comparisonMode === ComparisonMode.ValueSet &&
      a.totalOccurrences !== b.totalOccurrences
    ) {
      return b.totalOccurrences - a.totalOccurrences; // Descending by occurrences
    }

    // Tertiary: When value set sizes are equal, prefer fewer properties
    // (simpler definition should be the leader)
    if (
      a.comparisonMode === ComparisonMode.ValueSet &&
      b.comparisonMode === ComparisonMode.ValueSet &&
      a.propCount !== b.propCount
    ) {
      return a.propCount - b.propCount; // Ascending by property count
    }

    // Quaternary: Stable sort by original order
    return a.index - b.index;
  });

  return groupsDataWithMeta;
};

export const buildLeadingGroups = (
  groups: StylesGroup[],
  comparisonMode: ComparisonMode = ComparisonMode.DirectProps,
  actionName: StylesDerivedNativeActionNames,
  valueSetValueFilter?: ValueSetValueFilter,
): LeadingStyleGroup[] => {
  const groupsData = collectGroupsData(
    groups,
    comparisonMode,
    actionName,
    valueSetValueFilter,
  );

  const leadingGroups = buildLeadingGroupsFromResolved(groupsData);

  return leadingGroups;
};
